import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { parsearWikilinks, parsearWikilinksTitulos } from '../../lib/wikilinks';
import { analizarPensamiento, ejecutarComando, parseCommand } from '../../lib/copiloto';

const font = "'DM Sans', sans-serif";
const serif = "'DM Serif Display', serif";
const C = { turquesa:'#5dc1b9', mango:'#FDBE02', genio:'#D85A30', luca:'#185FA5' };

const COMANDOS = [
  { cmd: '[?web {tema}]', desc: 'Genio busca en internet', p: 'genio' },
  { cmd: '[?red {tema}]', desc: 'Genio busca en T&D', p: 'genio' },
  { cmd: '[!opinion]', desc: 'Luca te da opinion dura', p: 'luca' },
  { cmd: '[!plan]', desc: 'Luca te da plan de 7 dias', p: 'luca' },
  { cmd: '[+ideas]', desc: 'Genio lanza 5 chispas', p: 'genio' },
  { cmd: '[+conexiones]', desc: 'Genio sugiere wikilinks', p: 'genio' },
  { cmd: '[/resumen]', desc: 'Resume lo escrito', p: 'luca' },
];

export default function EditorPensamiento({ user, pensamientosUsuario = [], onPublicar, modo = 'pensamiento_publico', isMobile = false, pensamientoExistente = null }) {
  const esIdea = modo === 'idea_privada';
  const esEdicion = !!pensamientoExistente;
  const [titulo, setTitulo] = useState(pensamientoExistente?.titulo || '');
  const [publicado, setPublicado] = useState(false);
  const [contenido, setContenido] = useState(pensamientoExistente?.contenido || '');
  const [lucaTooltip, setLucaTooltip] = useState(false);
  const [tags, setTags] = useState(pensamientoExistente?.tags || []);
  const initRef = useRef(false);

  // Copiloto state
  const [showComandos, setShowComandos] = useState(false);
  const [copilotoResult, setCopilotoResult] = useState(null);
  const [copilotoLoading, setCopilotoLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const analyzeTimer = useRef(null);
  const [dropdownAbierto, setDropdownAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [posicionDropdown, setPosicionDropdown] = useState({ top: 0, left: 0 });
  const [guardando, setGuardando] = useState(false);
  const [showTip, setShowTip] = useState(false);
  const editorRef = useRef(null);
  const savedRangeRef = useRef(null);

  // Track if initial content was set (to avoid overwriting user edits)
  useEffect(() => {
    if (pensamientoExistente && editorRef.current) {
      initRef.current = true;
    }
  }, [pensamientoExistente]);

  // Auto-analyze for copiloto (debounced)
  const triggerAnalysis = useCallback((html) => {
    clearTimeout(analyzeTimer.current);
    analyzeTimer.current = setTimeout(async () => {
      const text = html.replace(/<[^>]*>/g, '');
      if (text.trim().length < 15) { setStats(null); setCopilotoResult(null); return; }
      const result = await analizarPensamiento(text);
      setStats(result.stats);
      if (result.observaciones.length > 0) setCopilotoResult(result.observaciones[0]);
    }, 1500);
  }, []);

  // Check for commands in text
  const checkCommand = useCallback(async (html) => {
    const text = html.replace(/<[^>]*>/g, '');
    const parsed = parseCommand(text);
    if (!parsed) return;
    if (editorRef.current) {
      editorRef.current.innerHTML = editorRef.current.innerHTML.replace(/\[[?!+/]\w+(?:\s+[^\]]*)?]/g, '');
      setContenido(editorRef.current.innerHTML);
    }
    setCopilotoLoading(true);
    const result = await ejecutarComando(parsed.comando, parsed.query, text);
    setCopilotoResult(result);
    setCopilotoLoading(false);
  }, []);

  // Live wikilink detection
  const conexionesDetectadas = useMemo(() => {
    const ids = parsearWikilinks(contenido);
    return pensamientosUsuario.filter(p => ids.includes(p.id));
  }, [contenido, pensamientosUsuario]);

  const handleInput = (e) => {
    const html = e.target.innerHTML;
    setContenido(html);
    triggerAnalysis(html);
    checkCommand(html);
    const text = e.target.textContent;
    const match = text.match(/\[\[([^\]]*)$/);
    if (match) {
      setBusqueda(match[1].toLowerCase());
      const sel = window.getSelection();
      if (sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const editorRect = editorRef.current.getBoundingClientRect();
        setPosicionDropdown({ top: rect.bottom - editorRect.top + 5, left: rect.left - editorRect.left });
      }
      const sel2 = window.getSelection();
      if (sel2.rangeCount > 0) savedRangeRef.current = sel2.getRangeAt(0).cloneRange();
      setDropdownAbierto(true);
    } else {
      setDropdownAbierto(false);
    }
  };

  const insertarWikilink = (pensamiento) => {
    const editor = editorRef.current;
    if (!editor) return;

    const range = savedRangeRef.current;
    if (!range) return;

    editor.focus();
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);

    const textNode = range.startContainer;
    if (textNode.nodeType === Node.TEXT_NODE) {
      const text = textNode.textContent;
      const cursorPos = range.startOffset;
      const beforeCursor = text.substring(0, cursorPos);
      const bracketIdx = beforeCursor.lastIndexOf('[[');
      if (bracketIdx !== -1) {
        textNode.textContent = text.substring(0, bracketIdx) + text.substring(cursorPos);
        range.setStart(textNode, bracketIdx);
        range.collapse(true);
      }
    }

    const span = document.createElement('span');
    span.style.cssText = 'color:#5dc1b9;background:rgba(93,193,185,0.12);padding:1px 5px;border-radius:4px;text-decoration:underline dotted;font-weight:500;';
    span.contentEditable = 'false';
    span.dataset.pensamientoId = pensamiento.id;
    span.className = 'wikilink';
    span.textContent = `[[${pensamiento.titulo}]]`;
    range.insertNode(span);

    const space = document.createTextNode('\u00A0');
    span.parentNode.insertBefore(space, span.nextSibling);
    const newRange = document.createRange();
    newRange.setStartAfter(space);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);

    setDropdownAbierto(false);
    savedRangeRef.current = null;
    setContenido(editor.innerHTML);
  };

  const pensamientosFiltrados = pensamientosUsuario.filter(p =>
    p.titulo.toLowerCase().includes(busqueda)
  ).slice(0, 5);

  const handlePublicar = async () => {
    const textoPlano = editorRef.current?.textContent?.trim() || '';
    if (!titulo.trim() || !textoPlano) return;
    if (!user?.id) { alert('Error: no estas autenticado.'); return; }
    setGuardando(true);
    const freshHtml = editorRef.current?.innerHTML || contenido;
    const wikilinkIds = parsearWikilinks(freshHtml);

    // Also resolve hand-typed [[Title]]
    const allWikilinkIds = [...wikilinkIds];
    const titulos = parsearWikilinksTitulos(freshHtml);
    for (const t of titulos) {
      const found = pensamientosUsuario.find(p => p.titulo?.toLowerCase() === t.toLowerCase() && !allWikilinkIds.includes(p.id));
      if (found) allWikilinkIds.push(found.id);
    }

    let nuevo;
    if (esEdicion) {
      const { data, error } = await supabase.from('pensamientos')
        .update({ titulo: titulo.trim(), contenido: freshHtml.trim(), tags })
        .eq('id', pensamientoExistente.id).select().single();
      if (error) { alert('Error al guardar: ' + error.message); setGuardando(false); return; }
      nuevo = data;
    } else {
      const { data, error } = await supabase.from('pensamientos').insert({
        user_id: user.id, titulo: titulo.trim(), contenido: freshHtml.trim(), tags,
        visibilidad: esIdea ? 'privado' : 'publico',
      }).select().single();
      if (error) { alert('Error al publicar: ' + (error.message || 'Intenta de nuevo')); setGuardando(false); return; }
      nuevo = data;

      if (allWikilinkIds.length > 0) {
        await supabase.from('sinapsis').insert(allWikilinkIds.map(toId => ({
          pensamiento_from_id: nuevo.id, pensamiento_to_id: toId, user_from_id: user.id, tipo: 'wikilink', score: 1.0,
        })));
      } else {
        await supabase.from('sinapsis').insert({
          pensamiento_from_id: nuevo.id, user_from_id: user.id, user_to_id: user.id, tipo: 'cluster', score: 1.0,
        });
      }
    }

    setGuardando(false);
    setPublicado(true);
    setTimeout(() => setPublicado(false), 3000);
    onPublicar?.(nuevo);
    if (!esEdicion) {
      setTitulo(''); setContenido(''); setTags([]);
      if (editorRef.current) editorRef.current.innerHTML = '';
    }
  };

  return (
    <div style={{ background:'rgba(255,255,255,0.012)', border:'0.5px solid rgba(255,255,255,0.08)', borderRadius:12, padding:'18px 22px', fontFamily:font, color:'#fff' }}>
      {/* Tip educativo colapsable */}
      <div style={{ marginBottom: 14 }}>
        <button onClick={() => setShowTip(!showTip)} style={{
          background:'none', border:'none', color:'rgba(255,255,255,0.45)', fontSize:11, cursor:'pointer',
          fontFamily:font, padding:0, display:'flex', alignItems:'center', gap:6,
        }}>
          <span style={{ fontSize: 13 }}>📍</span>
          <span style={{ textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>¿Cómo conectar tus pensamientos?</span>
          <span style={{ fontSize: 9, transform: showTip ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>▼</span>
        </button>
        {showTip && (
          <div style={{ marginTop: 8, padding: '12px 14px', background: 'rgba(93,193,185,0.06)', border: '0.5px solid rgba(93,193,185,0.15)', borderRadius: 10, fontSize: 12, color: 'rgba(255,255,255,0.6)', lineHeight: 1.7 }}>
            <p style={{ margin: '0 0 6px' }}>Usa <code style={{ background:'rgba(93,193,185,0.12)', color:C.turquesa, padding:'1px 5px', borderRadius:3 }}>[[ ]]</code> para enlazar con otros pensamientos.</p>
            <p style={{ margin: '0 0 8px', fontStyle: 'italic', color: 'rgba(255,255,255,0.45)' }}>"Hoy entendí algo sobre <span style={{ color: C.turquesa }}>[[El miedo al fracaso]]</span>"</p>
            <p style={{ margin: 0, fontSize: 11 }}>
              <span style={{ color: 'rgba(255,255,255,0.4)' }}>Sin [[ ]] → pensamiento independiente conectado a ti</span><br/>
              <span style={{ color: C.turquesa }}>Con [[ ]] → pensamiento en cadena conectado al otro</span>
            </p>
          </div>
        )}
      </div>

      <input type="text" placeholder="Título del pensamiento..." value={titulo} onChange={(e) => setTitulo(e.target.value)} maxLength={120}
        style={{ width:'100%', background:'transparent', border:0, color:'#fff', fontFamily:serif, fontSize:22, padding:'0 0 10px', outline:'none', marginBottom:6, borderBottom:'0.5px solid rgba(255,255,255,0.1)', boxSizing:'border-box' }} />

      <div style={{ display:'flex', gap:6, marginBottom:10, flexWrap:'wrap' }}>
        {tags.map((t, i) => <span key={i} style={{ padding:'3px 9px', background:'rgba(93,193,185,0.12)', color:C.turquesa, borderRadius:999, fontSize:11 }}>{t}</span>)}
        <input type="text" placeholder="+ tag" onKeyDown={(e) => {
          if (e.key === 'Enter' && e.target.value.trim()) { setTags([...tags, '#' + e.target.value.trim().replace(/^#/, '')]); e.target.value = ''; }
        }} style={{ background:'transparent', border:'0.5px solid rgba(255,255,255,0.1)', color:'rgba(255,255,255,0.7)', borderRadius:999, padding:'3px 9px', fontSize:11, fontFamily:'inherit', outline:'none' }} />
      </div>

      <div style={{ position:'relative' }}>
        <div ref={editorRef} contentEditable suppressContentEditableWarning onInput={handleInput} inputMode="text"
          dangerouslySetInnerHTML={esEdicion ? { __html: pensamientoExistente?.contenido || '' } : undefined}
          onPaste={(e) => {
            const pasted = (e.clipboardData || window.clipboardData).getData('text');
            if (pasted.length > 200) { setLucaTooltip(true); setTimeout(() => setLucaTooltip(false), 6000); }
          }}
          data-placeholder={esIdea ? 'Escribe tu idea... usa [[ ]] para conectar con otras ideas' : 'Escribe tu pensamiento... usa [[ ]] para conectar con otros'}
          style={{ minHeight:140, background:'rgba(255,255,255,0.02)', border:'0.5px solid rgba(255,255,255,0.1)', borderRadius:8, padding:14, fontSize:14, lineHeight:1.75, color:'rgba(255,255,255,0.9)', cursor:'text', outline:'none', WebkitUserSelect:'text', userSelect:'text', WebkitTouchCallout:'default' }} />

        {lucaTooltip && (
          <div style={{ position:'absolute', bottom:-44, left:0, right:0, padding:'8px 14px', background:'rgba(93,193,185,0.12)', border:'0.5px solid rgba(93,193,185,0.3)', borderRadius:10, fontSize:12, color:C.turquesa, animation:'fadeIn 0.3s ease', display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:16 }}>◆</span>
            <span>Luca: ¿Lo puedes decir en tus palabras? Así lo procesas mejor y tu yo del futuro lo va a recordar.</span>
          </div>
        )}

        {dropdownAbierto && pensamientosFiltrados.length > 0 && (
          <div style={{ position:'absolute', top:posicionDropdown.top, left:posicionDropdown.left, background:'#15151a', border:'0.5px solid rgba(93,193,185,0.4)', borderRadius:8, padding:6, minWidth:280, zIndex:10, boxShadow:'0 6px 20px rgba(0,0,0,0.5)' }}>
            <p style={{ margin:'4px 8px 6px', fontSize:9, color:C.turquesa, letterSpacing:1.2, textTransform:'uppercase' }}>Tus pensamientos · click para conectar</p>
            {pensamientosFiltrados.map((p) => (
              <div key={p.id} onMouseDown={(e) => { e.preventDefault(); insertarWikilink(p); }} style={{ padding:'8px 10px', borderRadius:5, cursor:'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background='rgba(93,193,185,0.15)'}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                <p style={{ margin:0, fontSize:12, color:'#fff', fontWeight:500 }}>✏️ {p.titulo}</p>
                <p style={{ margin:'1px 0 0 22px', fontSize:10, color:'rgba(255,255,255,0.5)' }}>{new Date(p.created_at).toLocaleDateString('es')}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Live connection indicator */}
      <div style={{ marginTop: 10, fontSize: 11, color: conexionesDetectadas.length > 0 ? C.turquesa : 'rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span>🔗 {conexionesDetectadas.length} conexion{conexionesDetectadas.length !== 1 ? 'es' : ''} detectada{conexionesDetectadas.length !== 1 ? 's' : ''}</span>
        {conexionesDetectadas.length > 0 && (
          <span style={{ color: 'rgba(255,255,255,0.45)' }}>
            — {conexionesDetectadas.map(p => p.titulo).join(', ')}
          </span>
        )}
      </div>

      {publicado && (
        <div style={{ marginTop:12, padding:'10px 14px', background:'rgba(253,190,2,0.12)', border:'0.5px solid rgba(253,190,2,0.3)', borderRadius:10, display:'flex', alignItems:'center', gap:8, animation:'fadeIn 0.3s ease' }}>
          <span style={{ fontSize:16 }}>✅</span>
          <p style={{ margin:0, fontSize:13, color:C.mango, fontWeight:500 }}>{esIdea ? 'Idea guardada ✨' : 'Publicado · 1 sinapsis nueva'}</p>
        </div>
      )}

      <div style={{ marginTop:16, display:'flex', alignItems:'center', justifyContent:'flex-end' }}>
        <button onClick={handlePublicar} disabled={guardando || !titulo.trim() || !(editorRef.current?.textContent?.trim())} style={{
          background:'linear-gradient(135deg, #5dc1b9, #FDBE02)', border:0, color:'#0a0a0a', padding:'9px 18px', borderRadius:8, fontSize:13, fontWeight:500, cursor:'pointer', display:'flex', alignItems:'center', gap:6, fontFamily:'inherit', opacity: (guardando || !titulo.trim() || !contenido.trim()) ? 0.5 : 1,
        }}>
          {guardando ? '⏳ Guardando...' : esEdicion ? '✅ Guardar cambios' : esIdea ? '💡 Guardar idea' : '🚀 Publicar pensamiento'}
        </button>
      </div>

      {/* Copiloto panel */}
      <div style={{ marginTop:12, background:'rgba(255,255,255,0.03)', border:'0.5px solid rgba(255,255,255,0.08)', borderRadius:12, padding:14 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ width:20, height:20, borderRadius:'50%', background:C.genio, display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:9, color:'#fff' }}>⚡</span>
            <span style={{ width:20, height:20, borderRadius:'50%', background:C.luca, display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:9, color:'#fff' }}>◆</span>
            <span style={{ fontSize:11, fontWeight:600, color:'rgba(255,255,255,0.6)' }}>Copiloto</span>
          </div>
          <span style={{ fontSize:9, padding:'2px 8px', borderRadius:50, background:'rgba(93,193,185,0.15)', color:C.turquesa, fontWeight:600 }}>EN VIVO</span>
        </div>

        {copilotoLoading && <p style={{ fontSize:11, color:'rgba(255,255,255,0.4)', margin:0 }}>Analizando...</p>}

        {copilotoResult && (
          <div style={{ padding:'8px 10px', background:`${copilotoResult.personaje === 'genio' ? C.genio : C.luca}10`, border:`0.5px solid ${copilotoResult.personaje === 'genio' ? C.genio : C.luca}30`, borderRadius:10, marginBottom:8 }}>
            <p style={{ margin:0, fontSize:11, color:'rgba(255,255,255,0.7)', lineHeight:1.6 }}>{copilotoResult.mensaje || copilotoResult.titulo}</p>
          </div>
        )}

        {stats && (
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:'rgba(255,255,255,0.4)', padding:'4px 0' }}>
            <span>📝 {stats.palabras} palabras</span>
            <span>⚠️ {stats.supuestos} supuestos</span>
            <span style={{ color: stats.claridad === 'alta' ? C.turquesa : C.mango }}>💎 {stats.claridad}</span>
          </div>
        )}

        {!copilotoLoading && !copilotoResult && !stats && (
          <p style={{ fontSize:11, color:'rgba(255,255,255,0.3)', margin:0, textAlign:'center', padding:'8px 0' }}>Escribe y Genio & Luca observaran.</p>
        )}
      </div>

      {/* Comandos disponibles */}
      <div style={{ marginTop:10 }}>
        <button onClick={() => setShowComandos(!showComandos)} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.4)', fontSize:11, cursor:'pointer', fontFamily:font, padding:0, display:'flex', alignItems:'center', gap:6 }}>
          <span>✨</span><span style={{ textDecoration:'underline dotted', textUnderlineOffset:3 }}>COMANDOS DISPONIBLES</span>
          <span style={{ fontSize:9, transform:showComandos?'rotate(180deg)':'none', transition:'transform 0.2s' }}>▼</span>
        </button>
        {showComandos && (
          <div style={{ marginTop:6, padding:'10px 12px', background:'rgba(255,255,255,0.03)', border:'0.5px solid rgba(255,255,255,0.08)', borderRadius:10 }}>
            {COMANDOS.map((c, i) => (
              <div key={i} style={{ display:'flex', gap:8, padding:'3px 0', fontSize:11, alignItems:'center' }}>
                <code style={{ background:c.p==='genio'?'rgba(216,90,48,0.12)':'rgba(24,95,165,0.12)', color:c.p==='genio'?C.genio:C.luca, padding:'1px 6px', borderRadius:4, fontSize:10, fontFamily:'monospace', minWidth:120 }}>{c.cmd}</code>
                <span style={{ color:'rgba(255,255,255,0.5)' }}>{c.desc}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Banner anti-copia */}
      <div style={{ marginTop:10, padding:'10px 12px', background:'rgba(255,255,255,0.02)', border:'0.5px solid rgba(255,255,255,0.06)', borderRadius:10 }}>
        <p style={{ margin:0, fontSize:10, color:'rgba(255,255,255,0.35)', lineHeight:1.7 }}>
          🛡️ Genio y Luca <strong style={{ color:'rgba(255,255,255,0.5)' }}>NO ESCRIBEN POR TI</strong>. Te hacen preguntas. Cuestionan tus supuestos. Te ayudan a afinar TU idea, no a generarla.
        </p>
      </div>

      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}
      .wikilink { color:#5dc1b9 !important; background:rgba(93,193,185,0.12); padding:1px 5px; border-radius:4px; text-decoration:underline dotted; font-weight:500; }
      [contenteditable]:empty::before { content: attr(data-placeholder); color: rgba(255,255,255,0.3); }`}</style>
    </div>
  );
}
